/**
 * ComposeModal — floating rich-text email composer (Gmail-style).
 * Bottom-right corner, minimisable, full-screen mode, rich text toolbar.
 * Features: schedule send, auto-signature injection, template picker.
 */

import { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, PaperPlaneTilt, Minus, ArrowsOut, ArrowsIn, Clock, Paperclip, Article } from '@phosphor-icons/react';
import { composeEmail, saveDraft, deleteDraft, type ComposeEmailInput } from '@/lib/inboxApi';
import { fetchTemplates } from '@/lib/inboxTemplatesApi';
import { nowLocalDatetime } from '@/lib/datetime';
import { fetchDefaultSignatures } from '@/lib/inboxSignaturesApi';
import { RichTextEditor } from './RichTextEditor';
import { RecipientAutocomplete } from './RecipientAutocomplete';

interface Props {
  fromAddress: string;
  defaultTo?: string;
  defaultSubject?: string;
  defaultBody?: string;
  matterId?: string;
  matterName?: string;
  onClose: () => void;
}

// ─── Schedule-send presets ────────────────────────────────────

function getTomorrowMorning(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(8, 0, 0, 0);
  return d.toISOString().slice(0, 16); // datetime-local format
}

function getNextMondayMorning(): string {
  const d = new Date();
  const day = d.getDay();
  const daysUntilMonday = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + daysUntilMonday);
  d.setHours(9, 0, 0, 0);
  return d.toISOString().slice(0, 16);
}

// ─── Compact signature block appended to body ─────────────────

function wrapSignature(sigBody: string): string {
  return `<p><br></p><div style="border-top:1px solid var(--border-default);margin-top:8px;padding-top:8px;color:#6b7280">${sigBody}</div>`;
}

// ─── ComposeModal ─────────────────────────────────────────────

export function ComposeModal({
  fromAddress, defaultTo = '', defaultSubject = '', defaultBody = '',
  matterId, matterName, onClose,
}: Props) {
  const queryClient = useQueryClient();
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [bodyHtml, setBodyHtml] = useState(defaultBody);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [minimised, setMinimised] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState<string>(''); // ISO datetime-local
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 25 MB per-file ceiling (matches the API multer limit)
  const MAX_FILE_BYTES = 25 * 1024 * 1024;

  function handleFilesPicked(fileList: FileList | null) {
    if (!fileList?.length) return;
    const incoming = Array.from(fileList);
    const tooBig = incoming.filter((f) => f.size > MAX_FILE_BYTES);
    if (tooBig.length) {
      toast.error(`${tooBig.map((f) => f.name).join(', ')} exceeds the 25 MB limit`);
    }
    const accepted = incoming.filter((f) => f.size <= MAX_FILE_BYTES);
    setAttachments((prev) => [...prev, ...accepted]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeAttachment(index: number) {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }

  function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // Fetch default signature for new emails
  const { data: defaultSigs, isLoading: sigsLoading } = useQuery({
    queryKey: ['inbox-signature-defaults'],
    queryFn: fetchDefaultSignatures,
    staleTime: 300_000,
    refetchOnWindowFocus: false,
  });

  const { data: templates = [] } = useQuery({
    queryKey: ['inbox-templates'],
    queryFn: fetchTemplates,
    enabled: showTemplates,
  });

  // ─── Draft autosave ──────────────────────────────────────────
  // Mirrors Gmail/Outlook: the message is persisted as a draft a couple of
  // seconds after the barrister stops typing, so it survives a reload and
  // appears in the Drafts folder. The draft id is held in a ref and reused on
  // each save; it is deleted once the message is actually sent.
  const draftIdRef = useRef<string | null>(null);
  const savedSnapshotRef = useRef<string>('');
  const draftSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function snapshot(): string {
    return JSON.stringify({ to, cc, bcc, subject, bodyHtml });
  }
  function hasContentToSave(): boolean {
    return Boolean(to.trim() || subject.trim() || (bodyHtml && bodyHtml.replace(/<[^>]*>/g, '').trim()));
  }

  useEffect(() => {
    const snap = snapshot();
    if (snap === savedSnapshotRef.current || !hasContentToSave()) return;
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(async () => {
      try {
        const saved = await saveDraft({
          to: to ? parseAddresses(to) : undefined,
          cc: cc ? parseAddresses(cc) : undefined,
          bcc: bcc ? parseAddresses(bcc) : undefined,
          subject: subject.trim() || undefined,
          bodyHtml: bodyHtml || undefined,
          matterId: matterId || undefined,
        }, draftIdRef.current ?? undefined);
        draftIdRef.current = saved.id;
        savedSnapshotRef.current = snap;
      } catch { /* transient autosave failure — will retry on next change */ }
    }, 2000);
    return () => { if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current); };
  }, [to, cc, bcc, subject, bodyHtml, matterId]);

  const mutation = useMutation({
    mutationFn: (input: ComposeEmailInput) => composeEmail(input),
    onSuccess: (_, vars) => {
      if (vars.scheduledAt) {
        toast.success('Email scheduled');
      } else {
        toast.success('Email sent');
      }
      // Remove the autosaved draft now that it has been sent
      if (draftIdRef.current) {
        void deleteDraft(draftIdRef.current).catch(() => { /* best effort */ });
        draftIdRef.current = null;
      }
      queryClient.invalidateQueries({ queryKey: ['inbox-emails'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['folder-counts'] });
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to send email');
    },
  });

  function parseAddresses(raw: string): string[] {
    return raw.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
  }

  function handleSend(scheduled?: string) {
    const toList = parseAddresses(to);
    if (!toList.length) { toast.error('Add at least one recipient'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }

    if (scheduled && attachments.length) {
      toast.error('Scheduling with attachments is not supported. Send now, or remove the files.');
      return;
    }

    mutation.mutate({
      to: toList,
      cc: cc ? parseAddresses(cc) : undefined,
      bcc: bcc ? parseAddresses(bcc) : undefined,
      subject: subject.trim(),
      bodyHtml: bodyHtml || undefined,
      bodyText: bodyHtml ? bodyHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : undefined,
      matterId: matterId || undefined,
      scheduledAt: scheduled ? new Date(scheduled).toISOString() : undefined,
      attachments: attachments.length ? attachments : undefined,
    });
  }

  function applyTemplate(t: { subject: string; body?: string }) {
    if (!subject) setSubject(t.subject);
    if (t.body) setBodyHtml(t.body);
    setShowTemplates(false);
  }

  // Build the initial body: user content (if any) + signature appended below
  const sigHtml = defaultSigs?.forNew?.body ? wrapSignature(defaultSigs.forNew.body) : '';
  const initialBody = defaultBody
    ? `${defaultBody}${sigHtml}`
    : sigHtml;

  const inputClass = 'flex h-8 w-full rounded border border-border bg-transparent px-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none';

  const containerStyle: React.CSSProperties = fullscreen ? {
    position: 'fixed', inset: 0, zIndex: 60,
    display: 'flex', flexDirection: 'column',
    background: 'var(--surface-card)', borderRadius: 0,
    boxShadow: 'none',
  } : {
    position: 'fixed', bottom: 0, right: 24, zIndex: 50,
    width: 540, maxHeight: minimised ? 44 : 580,
    display: 'flex', flexDirection: 'column',
    background: 'var(--surface-card)',
    borderRadius: '12px 12px 0 0',
    border: '1px solid var(--border-strong)',
    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
    overflow: 'hidden',
    transition: 'max-height 0.15s ease',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#111827', padding: '10px 14px', flexShrink: 0,
      }}>
        <div>
          <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, fontWeight: 600, color: 'white' }}>
            {scheduledAt ? 'Schedule Email' : 'New Message'}
          </span>
          {matterName && (
            <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, color: 'var(--text-subtle)', marginLeft: 8 }}>
              · {matterName}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={() => setMinimised((v) => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center' }} title="Minimise">
            <Minus className="h-4 w-4" />
          </button>
          <button onClick={() => setFullscreen((v) => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center' }} title={fullscreen ? 'Exit full screen' : 'Full screen'}>
            {fullscreen ? <ArrowsIn className="h-4 w-4" /> : <ArrowsOut className="h-4 w-4" />}
          </button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center' }}>
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!minimised && (
        <>
          {/* From */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ width: 48, fontSize: 12, color: 'var(--text-subtle)', flexShrink: 0 }}>From</span>
            <span style={{
              width: 22, height: 22, borderRadius: '50%', background: '#2563eb', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, fontWeight: 600, flexShrink: 0,
            }}>
              {(fromAddress.trim()[0] ?? '?').toUpperCase()}
            </span>
            <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{fromAddress}</span>
          </div>

          {/* To */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ width: 48, fontSize: 12, color: 'var(--text-subtle)', flexShrink: 0 }}>To</span>
            <RecipientAutocomplete
              value={to}
              onChange={setTo}
              placeholder="Name, email, phone or matter…"
              className={inputClass}
            />
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {!showCc && <button onClick={() => setShowCc(true)} style={{ fontSize: 11, color: 'var(--text-subtle)', background: 'none', border: 'none', cursor: 'pointer' }}>Cc</button>}
              {!showBcc && <button onClick={() => setShowBcc(true)} style={{ fontSize: 11, color: 'var(--text-subtle)', background: 'none', border: 'none', cursor: 'pointer' }}>Bcc</button>}
            </div>
          </div>

          {showCc && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ width: 48, fontSize: 12, color: 'var(--text-subtle)', flexShrink: 0 }}>Cc</span>
              <RecipientAutocomplete value={cc} onChange={setCc} placeholder="Name, email, phone or matter…" className={inputClass} />
            </div>
          )}

          {showBcc && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ width: 48, fontSize: 12, color: 'var(--text-subtle)', flexShrink: 0 }}>Bcc</span>
              <RecipientAutocomplete value={bcc} onChange={setBcc} placeholder="Name, email, phone or matter…" className={inputClass} />
            </div>
          )}

          {/* Subject */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ width: 48, fontSize: 12, color: 'var(--text-subtle)', flexShrink: 0 }}>Subject</span>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className={inputClass}
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            />
          </div>

          {/* Schedule banner */}
          {scheduledAt && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '6px 14px', background: 'var(--surface-selected)', borderBottom: '1px solid #bfdbfe',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Clock size={12} color="#2563eb" />
                <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: '#1e40af' }}>
                  Scheduled: {new Date(scheduledAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <button
                onClick={() => setScheduledAt('')}
                style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Clear
              </button>
            </div>
          )}

          {/* Rich text editor — wait for sig to load so defaultValue is right */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            {!sigsLoading && (
              <RichTextEditor
                placeholder="Compose your message…"
                defaultValue={initialBody}
                onHtmlChange={setBodyHtml}
                minHeight={fullscreen ? 400 : 180}
                autoFocus
              />
            )}
          </div>

          {/* Templates dropdown */}
          {showTemplates && (
            <div style={{
              position: 'absolute', bottom: 52, right: 14,
              width: 320, maxHeight: 300, overflow: 'auto',
              background: 'var(--surface-card)', border: '1px solid var(--border-default)',
              borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              zIndex: 10,
            }}>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', fontSize: 12, color: 'var(--text-subtle)', fontWeight: 600 }}>
                Templates
              </div>
              {templates.length === 0 ? (
                <div style={{ padding: '16px 12px', fontSize: 13, color: 'var(--text-subtle)', textAlign: 'center' }}>No templates yet</div>
              ) : templates.map((t) => (
                <div
                  key={t.id}
                  onClick={() => applyTemplate(t)}
                  style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-subtle)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 2 }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{t.subject}</div>
                </div>
              ))}
            </div>
          )}

          {/* Schedule dropdown */}
          {showSchedule && (
            <div style={{
              position: 'absolute', bottom: 52, left: 14,
              width: 240,
              background: 'var(--surface-card)', border: '1px solid var(--border-default)',
              borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              zIndex: 10, overflow: 'hidden',
            }}>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', fontSize: 12, color: 'var(--text-subtle)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Schedule Send
              </div>
              {[
                { label: 'Tomorrow 8 AM', value: getTomorrowMorning() },
                { label: 'Monday 9 AM', value: getNextMondayMorning() },
              ].map((preset) => (
                <div
                  key={preset.label}
                  onClick={() => { setScheduledAt(preset.value); setShowSchedule(false); }}
                  style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-subtle)', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, color: 'var(--text-primary)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ fontWeight: 500 }}>{preset.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 2 }}>
                    {new Date(preset.value).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ))}
              <div style={{ padding: '10px 14px' }}>
                <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-subtle)', marginBottom: 6 }}>Custom time</div>
                <input
                  type="datetime-local"
                  min={nowLocalDatetime()}
                  step={60}
                  onChange={(e) => { if (e.target.value) { setScheduledAt(e.target.value); setShowSchedule(false); } }}
                  style={{
                    width: '100%', padding: '5px 8px', border: '1px solid var(--border-strong)',
                    borderRadius: 6, fontSize: 12, fontFamily: "'IBM Plex Sans', sans-serif",
                    color: 'var(--text-primary)', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
          )}

          {/* Attached files */}
          {attachments.length > 0 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 14px',
              borderTop: '1px solid var(--border-default)', flexShrink: 0, background: 'var(--surface-card)',
            }}>
              {attachments.map((file, i) => (
                <div
                  key={`${file.name}-${i}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'var(--surface-hover)', border: '1px solid var(--border-default)',
                    borderRadius: 6, padding: '4px 8px', maxWidth: 220,
                  }}
                >
                  <Paperclip size={13} color="#6b7280" style={{ flexShrink: 0 }} />
                  <span style={{
                    fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-primary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {file.name}
                  </span>
                  <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, color: 'var(--text-subtle)', flexShrink: 0 }}>
                    {formatBytes(file.size)}
                  </span>
                  <button
                    onClick={() => removeAttachment(i)}
                    title="Remove attachment"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', display: 'flex', flexShrink: 0, padding: 0 }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Bottom toolbar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 14px', borderTop: '1px solid var(--border-default)', flexShrink: 0, background: 'var(--surface-card)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {/* Send / Send scheduled */}
              <button
                onClick={() => handleSend(scheduledAt || undefined)}
                disabled={mutation.isPending}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '7px 16px', borderRadius: 20,
                  background: '#2563eb', color: 'white',
                  border: 'none', cursor: 'pointer',
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: 13, fontWeight: 600,
                  opacity: mutation.isPending ? 0.6 : 1,
                }}
              >
                <PaperPlaneTilt className="h-4 w-4" weight="fill" />
                {mutation.isPending ? 'Sending…' : scheduledAt ? 'Schedule' : 'Send'}
              </button>

              {/* Schedule send */}
              <button
                onClick={() => { setShowSchedule((v) => !v); setShowTemplates(false); }}
                title="Schedule send"
                style={{
                  width: 32, height: 32, borderRadius: 6, border: 'none',
                  background: showSchedule || scheduledAt ? '#eff6ff' : 'none',
                  cursor: 'pointer',
                  color: scheduledAt ? '#2563eb' : '#6b7280',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Clock size={15} />
              </button>

              {/* Template button */}
              <button
                onClick={() => { setShowTemplates((v) => !v); setShowSchedule(false); }}
                title="Insert template"
                style={{
                  width: 32, height: 32, borderRadius: 6, border: 'none',
                  background: showTemplates ? 'var(--surface-sunken)' : 'none',
                  cursor: 'pointer', color: 'var(--text-muted)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Article size={16} />
              </button>

              {/* Attach file — real picker */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleFilesPicked(e.target.files)}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                title="Attach file"
                style={{
                  width: 32, height: 32, borderRadius: 6, border: 'none',
                  background: attachments.length ? '#eff6ff' : 'none', cursor: 'pointer',
                  color: attachments.length ? '#2563eb' : 'var(--text-muted)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Paperclip size={16} />
              </button>
            </div>

            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center' }}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
